import * as Popover from '@radix-ui/react-popover';
import type { PersonRef, StatusOption } from '@mona-roadmap/domain';
import { Calendar, X } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { useFieldState } from '../../lib/fieldState';
import { formatDate, formatDateFull } from '../../lib/format';
import type { OptionItem } from '../ui/OptionList';
import { MultiSelect, SingleSelect } from '../ui/Pickers';
import { AvatarStack, FieldStatusIcon, SprintPill, StatusChip, StatusDot } from '../ui/Visuals';

export const optionItems = (options: StatusOption[], colorOf?: (label: string) => string | undefined): OptionItem[] =>
  [...options].sort((a, b) => a.index - b.index).map((o) => ({ value: o.label, label: o.label, color: colorOf ? colorOf(o.label) : o.color }));

export const personItems = (people: PersonRef[]): OptionItem[] =>
  people.map((p) => ({ value: p.id, label: p.name, person: p, hint: p.kind === 'team' ? 'team' : undefined }));

export type ValueStyle = 'chip' | 'dot' | 'sprint' | 'text';

const triggerClass = (state: ReturnType<typeof useFieldState>, extra = ''): string =>
  `cell-btn ${state?.status === 'saved' ? 'fs-saved' : ''} ${state?.status === 'error' ? 'fs-error' : ''} ${extra}`;

interface SelectProps {
  /** Human name of the field (for accessible names). */
  field: string;
  value: string | null;
  options: StatusOption[];
  style: ValueStyle;
  stateKey: string;
  onChange: (value: string | null) => void;
  color?: string;
  /** Overrides the monday colour per label (e.g. In progress is blue). */
  colorOf?: (label: string, mondayColor?: string) => string | undefined;
  sprintColors?: Record<string, { bg: string; fg: string }>;
  readOnly?: boolean;
  /** Full-width "form field" look (drawer) instead of the compact cell button. */
  block?: boolean;
  lazy?: boolean;
  clearLabel?: string;
}

/** Inline editor for single-select fields (status, scoping status, sprint, product area, type). */
export function SelectField({ field, value, options, style, stateKey, onChange, color, colorOf, sprintColors, readOnly, block, lazy = true, clearLabel = 'No value' }: SelectProps) {
  const state = useFieldState(stateKey);
  const saving = state?.status === 'saving';
  const optionColor = (label: string | null) => {
    const base = label ? options.find((o) => o.label === label)?.color : undefined;
    return label && colorOf ? colorOf(label, base) : base;
  };

  let display: ReactNode;
  if (value === null || value === '') display = <span className="text-faint">{block ? 'Not set' : '-'}</span>;
  else if (style === 'dot') display = <StatusDot label={value} color={color ?? optionColor(value)} field={field} />;
  else if (style === 'sprint') display = <SprintPill label={value} colors={sprintColors?.[value]} />;
  else if (style === 'chip') display = <StatusChip label={value} color={color ?? optionColor(value)} />;
  else display = <span className="truncate">{value}</span>;

  if (readOnly) return <span className="inline-flex min-w-0 items-center gap-1">{display}</span>;

  const items = optionItems(options, style === 'sprint' ? (l) => sprintColors?.[l]?.bg : (l) => optionColor(l));
  const trigger = (
    <button
      type="button"
      tabIndex={-1}
      className={triggerClass(state, block ? '!m-0 !h-8 w-full !justify-start !border-line-strong !bg-app !px-2' : '')}
      aria-label={`${field}: ${value ?? 'none'}. Edit`}
      aria-busy={saving || undefined}
      disabled={saving}
      onDoubleClick={(e) => e.stopPropagation()}
    >
      {display}
      <FieldStatusIcon state={state} />
    </button>
  );
  return <SingleSelect trigger={trigger} items={items} label={field} value={value} onChange={onChange} clearLabel={clearLabel} lazy={lazy} />;
}

interface PeopleProps {
  field: string;
  people: PersonRef[];
  all: PersonRef[];
  stateKey: string;
  onCommit: (ids: string[]) => void;
  readOnly?: boolean;
  block?: boolean;
  lazy?: boolean;
  max?: number;
}

/** Multi-person editor (assignees, PM owners, R&D owners). */
export function PeopleField({ field, people, all, stateKey, onCommit, readOnly, block, lazy = true, max }: PeopleProps) {
  const state = useFieldState(stateKey);
  const saving = state?.status === 'saving';
  const display =
    people.length === 0 ? (
      <span className="text-faint">{block ? 'Not set' : '-'}</span>
    ) : block ? (
      <span className="flex items-center gap-2">
        <AvatarStack people={people} max={max ?? 5} />
        <span className="truncate text-[14px]">{people.map((p) => p.name).join(', ')}</span>
      </span>
    ) : (
      <AvatarStack people={people} max={max} />
    );
  if (readOnly) return <span className="inline-flex items-center">{display}</span>;
  const trigger = (
    <button
      type="button"
      tabIndex={-1}
      className={triggerClass(state, block ? '!m-0 !h-8 w-full !justify-start !border-line-strong !bg-app !px-2' : '')}
      aria-label={`${field}: ${people.length ? people.map((p) => p.name).join(', ') : 'none'}. Edit`}
      aria-busy={saving || undefined}
      disabled={saving}
      onDoubleClick={(e) => e.stopPropagation()}
    >
      {display}
      <FieldStatusIcon state={state} />
    </button>
  );
  return <MultiSelect trigger={trigger} items={personItems(all)} label={field} values={people.map((p) => p.id)} onCommit={onCommit} lazy={lazy} />;
}

interface DateProps {
  field: string;
  value: string | null;
  stateKey: string;
  onChange: (value: string | null) => void;
  overdue?: boolean;
  prefix?: ReactNode;
  block?: boolean;
  readOnly?: boolean;
  /** Render as a "Target Oct 11, 2026" label (used next to a product goal's name). */
  variant?: 'label';
}

/** Date editor: native date input in a popover, saved on change; Clear removes the value. */
export function DateField({ field, value, stateKey, onChange, overdue, prefix, block, readOnly, variant }: DateProps) {
  const state = useFieldState(stateKey);
  const saving = state?.status === 'saving';
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState(value ?? '');
  const toggle = (next: boolean) => {
    if (next) setDraft(value ?? '');
    else if (draft && draft !== value) onChange(draft);
    setOpen(next);
  };
  const display = variant === 'label' ? (
    value ? (
      <span className={`target-label ${overdue ? 'overdue' : ''}`} title={overdue ? 'Target date has passed' : 'Target date'}>
        <span className="opacity-70">Target</span> {formatDateFull(value)}
      </span>
    ) : (
      <span className="target-label empty">Set target date</span>
    )
  ) : value ? (
    <span className={overdue ? 'text-danger' : ''} title={overdue ? 'Overdue' : undefined}>
      {prefix}
      {formatDate(value)}
    </span>
  ) : (
    <span className="text-faint">{block ? 'Not set' : '-'}</span>
  );
  if (readOnly) return display;
  return (
    <Popover.Root open={open} onOpenChange={toggle}>
      <Popover.Trigger asChild>
        <button
          type="button"
          tabIndex={-1}
          className={triggerClass(state, block ? '!m-0 !h-8 w-full !justify-start !border-line-strong !bg-app !px-2' : variant === 'label' ? '!m-0 !h-auto !border-0 !bg-transparent !p-0' : '')}
          aria-label={`${field}: ${value ?? 'none'}. Edit`}
          aria-busy={saving || undefined}
          disabled={saving}
          onDoubleClick={(e) => e.stopPropagation()}
        >
          {block ? <Calendar className="size-3.5 text-faint" aria-hidden /> : null}
          {display}
          <FieldStatusIcon state={state} />
        </button>
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content className="pop p-2" align="start" sideOffset={4} collisionPadding={8} aria-label={field}>
          <div className="flex items-center gap-2">
            <input
              type="date"
              className="input !w-[150px]"
              aria-label={`${field} date`}
              autoFocus
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') toggle(false);
              }}
            />
            <button type="button" className="btn btn-primary" onClick={() => toggle(false)}>
              Save
            </button>
            {value ? (
              <button
                type="button"
                className="btn btn-ghost !px-2"
                onClick={() => {
                  setDraft('');
                  setOpen(false);
                  onChange(null);
                }}
              >
                <X className="size-3.5" aria-hidden /> Clear
              </button>
            ) : null}
          </div>
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}
